import type { Mapping } from '../types';

// Derives an Integration-facing view over the existing Mapping resource
// (roadmap #9 Phase 1, slice: nav + Installed-integrations list). Phase 1
// had no lifecycle/health/run-record concept anywhere in the stack (see
// docs/INTEGRATION_TOOL_PHASE0_AUDIT.md R7), so every state below defaulted
// to a guess derived from static `spec` fields. Phase 2 added a real
// run-record backend (internal/integrationrun, surfaced by ActivitySection),
// so callers that already have that activity loaded should pass
// `hasDelivered` - otherwise a webhook mapping keeps reading "waiting for
// first event" forever, even after real deliveries have landed.

export type IntegrationLifecycleState = 'configured' | 'waiting_first_event' | 'paused' | 'configuration_incomplete';

export interface IntegrationView {
  mapping: Mapping;
  // 'webhook' | 'prometheus_remote_write' | ... - the raw sourceType,
  // unchanged. A typed ConnectorDefinition catalog is Phase 1's D4-gated
  // piece and is intentionally not built by this slice.
  connectorId: string;
  destinationChannel: string;
  state: IntegrationLifecycleState;
}

// A mapping only does something once it names where its Signals go.
function destinationChannelOf(mapping: Mapping): string {
  const ch = mapping.spec?.channel;
  return typeof ch === 'string' ? ch.trim() : '';
}

// Push-based sources (an inbound webhook) have a meaningful "waiting for
// the first delivery" moment. Pull-based sources (Prometheus remote_write,
// an Agent-side scrape/file import) are periodic, not event-driven, so
// "waiting for first event" would overclaim knowledge Phase 1 doesn't
// have - "configured" is the honest floor state for those instead.
function isPushBased(mapping: Mapping): boolean {
  return mapping.sourceType === 'webhook';
}

export function deriveIntegrationView(mapping: Mapping, opts?: { hasDelivered?: boolean }): IntegrationView {
  const destinationChannel = destinationChannelOf(mapping);
  // A paused mapping (mapping.enabled === false - mirrors the backend's
  // internal/integration.Lifecycle) reads as paused regardless of how it's
  // otherwise configured, same as the backend derivation.
  const state: IntegrationLifecycleState =
    mapping.enabled === false
      ? 'paused'
      : !destinationChannel
        ? 'configuration_incomplete'
        : isPushBased(mapping) && !opts?.hasDelivered
          ? 'waiting_first_event'
          : 'configured';

  return {
    mapping,
    connectorId: mapping.sourceType || 'custom',
    destinationChannel,
    state,
  };
}

export const integrationStateCopy: Record<IntegrationLifecycleState, { label: string; description: string }> = {
  configured: {
    label: 'Configured',
    description: 'Set up and ready. Open Activity to see what has actually arrived.',
  },
  waiting_first_event: {
    label: 'Waiting for first event',
    description: 'No delivery has been recorded yet for this integration.',
  },
  paused: {
    label: 'Paused',
    description: 'Ingestion is paused.',
  },
  configuration_incomplete: {
    label: 'Configuration incomplete',
    description: 'No destination channel is set, so incoming data has nowhere to go.',
  },
};
